import { FastCheckoutEvents, trackEvent } from "@punchup/analytics";
import { SentryFeatureTags, logMessage } from "@punchup/sentry";

// The three ways the iframe gets revealed. Only the first two prove the widget
// actually rendered content; "load-fallback" is the blind 4s reveal that fires
// when no message ever arrived — the blank-iframe case this telemetry exists
// to measure.
export type WidgetReadySource =
  | "page-ready"
  | "legacy-height"
  | "load-fallback";

// How long each load cycle gets before it is called "timed out". Deliberately
// shorter than the component's 4s blind reveal so a timeout always resolves
// (retry or give up) before an empty iframe gets faded in.
export const READY_TIMEOUT_MS = 3000;

// How many silent forced reloads a DEAD load gets before the fallback panel
// shows. Dead = no signal from the widget at all (no widget-ready, no height);
// an alive-but-slow widget is never reloaded — a reload would restart a load
// that is working — it goes straight to the panel instead.
export const MAX_LOAD_RETRIES = 3;

/**
 * Page/session context attached to every event. Read through a getter at emit
 * time (not snapshotted at start) because the component resolves the widget
 * origin and show in effects whose ordering relative to the open effect we'd
 * rather not depend on.
 */
export interface WidgetLoadContext {
  show_id: string | null;
  show_title: string | null;
  tixologi_event_id: string | null;
  venue_id: string | null;
  venue_name: string | null;
  widget_origin: string | null;
  hostname: string | null;
  is_mobile_view: boolean;
}

export interface WidgetLoadCallbacks {
  /**
   * A dead cycle timed out with retries left — the component force-reloads the
   * iframe src. The session has already begun timing the new cycle.
   */
  onRetry: () => void;
  /**
   * Final timeout: retries exhausted, or the widget is alive but stuck (a
   * reload would hurt, not help). The component shows the fallback panel.
   */
  onGiveUp: () => void;
}

// Durations use performance.now() (monotonic, unaffected by clock changes) —
// the repo's dayjs rule is about calendar dates, not stopwatch intervals; this
// follows the VideoLogger precedent in @punchup/video.
const now = () => performance.now();

/**
 * One Fast Checkout open = one session; "Buy more tickets" re-times as a new
 * attempt within the same session, and each forced reload of a dead load is a
 * retry cycle within the attempt. Plain TS (no React) so the once-bound
 * message listener in TixologiWidgetIframe can drive it through a single ref,
 * and so the timer/latch/retry matrix is unit-testable without rendering the
 * modal.
 */
export class WidgetLoadTelemetrySession {
  private active = false;
  private attempt = 0;
  private retry = 0;
  private attemptStartedAt = 0;
  private cycleStartedAt = 0;
  private msToIframeLoad: number | null = null;
  private msToWidgetReady: number | null = null;
  private msToFirstHeight: number | null = null;
  private realReady = false;
  private loadedEmitted = false;
  private timedOut = false;
  private timeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private getContext: () => WidgetLoadContext = () => ({
    show_id: null,
    show_title: null,
    tixologi_event_id: null,
    venue_id: null,
    venue_name: null,
    widget_origin: null,
    hostname: null,
    is_mobile_view: false,
  });
  private callbacks: WidgetLoadCallbacks = {
    onRetry: () => {},
    onGiveUp: () => {},
  };

  /**
   * Begin timing an open. Idempotent while a session is active: the open
   * branch of the [isOpen] effect has no cleanup, so React strict mode runs it
   * twice in dev — the second call must not reset the clock, re-arm a second
   * timer, or emit anything (start never emits).
   */
  start(getContext: () => WidgetLoadContext, callbacks: WidgetLoadCallbacks) {
    if (this.active) return;
    this.getContext = getContext;
    this.callbacks = callbacks;
    this.active = true;
    this.attempt = 1;
    this.beginAttempt();
  }

  /** "Buy more tickets" resets the iframe src — time the reload as a new attempt. */
  restart() {
    if (!this.active) return;
    this.attempt += 1;
    this.beginAttempt();
  }

  private beginAttempt() {
    this.retry = 0;
    this.attemptStartedAt = now();
    this.realReady = false;
    this.loadedEmitted = false;
    this.timedOut = false;
    this.beginCycle();
  }

  private beginCycle() {
    this.cycleStartedAt = now();
    this.msToIframeLoad = null;
    this.msToWidgetReady = null;
    this.msToFirstHeight = null;
    this.clearTimer();
    this.timeoutTimer = setTimeout(() => {
      this.timeoutTimer = null;
      this.handleTimeout();
    }, READY_TIMEOUT_MS);
  }

  private handleTimeout() {
    this.timedOut = true;
    // A widget that has posted ANYTHING (its JS booted, or its DOM rendered)
    // is alive and slow, not dead — a forced reload would restart a load that
    // is working. Only dead cycles are retried.
    const widgetAlive =
      this.msToWidgetReady !== null || this.msToFirstHeight !== null;
    const willRetry = !widgetAlive && this.retry < MAX_LOAD_RETRIES;
    const props = {
      ...this.sharedProps(),
      elapsed_ms: Math.round(now() - this.cycleStartedAt),
      ...this.milestoneFlags(),
      will_retry: willRetry,
    };
    trackEvent(FastCheckoutEvents.WIDGET_LOAD_TIMEOUT, props);
    if (willRetry) {
      this.retry += 1;
      this.beginCycle();
      this.callbacks.onRetry();
      return;
    }
    // Final give-up — the one alertable moment per attempt (retry timeouts are
    // Mixpanel-only): the buyer is now looking at the fallback panel.
    logMessage("Fast Checkout widget load timeout", {
      level: "warning",
      tags: { feature: SentryFeatureTags.Checkout },
      extra: { ...props, total_wait_ms: this.totalWaitMs() },
    });
    this.callbacks.onGiveUp();
  }

  /** The iframe document finished loading (network-level onLoad). */
  recordIframeLoad() {
    if (!this.active || this.msToIframeLoad !== null) return;
    this.msToIframeLoad = Math.round(now() - this.cycleStartedAt);
  }

  /** The widget posted `punchup-widget-ready` — its JS booted. */
  recordWidgetReady() {
    if (!this.active || this.msToWidgetReady !== null) return;
    this.msToWidgetReady = Math.round(now() - this.cycleStartedAt);
  }

  /** First numeric `height` message this cycle — the widget DOM rendered something. */
  recordHeightSeen() {
    if (!this.active || this.msToFirstHeight !== null) return;
    this.msToFirstHeight = Math.round(now() - this.cycleStartedAt);
  }

  /**
   * The iframe got revealed. Real-content sources (page-ready / legacy-height)
   * cancel the timeout; the blind load-fallback reveal never counts as success.
   * WIDGET_LOADED is emitted once per attempt, stamped with the FIRST reveal's
   * source — a later real signal upgrades the session to real-ready (so close
   * won't log an abandonment) without re-emitting.
   */
  recordReady(source: WidgetReadySource) {
    if (!this.active) return;
    if (source !== "load-fallback") {
      this.realReady = true;
      this.clearTimer();
    }
    if (this.loadedEmitted) return;
    this.loadedEmitted = true;
    trackEvent(FastCheckoutEvents.WIDGET_LOADED, {
      ...this.sharedProps(),
      ready_source: source,
      duration_ms: Math.round(now() - this.cycleStartedAt),
      total_wait_ms: this.totalWaitMs(),
      ms_to_iframe_load: this.msToIframeLoad,
      ms_to_widget_ready: this.msToWidgetReady,
      ms_to_first_height: this.msToFirstHeight,
      timed_out_first: this.timedOut,
    });
  }

  /** Modal closed. Logs an abandonment if the widget never proved real content. */
  recordClosed() {
    if (!this.active) return;
    if (!this.realReady) {
      trackEvent(FastCheckoutEvents.WIDGET_CLOSED_BEFORE_READY, {
        ...this.sharedProps(),
        elapsed_ms: Math.round(now() - this.cycleStartedAt),
        total_wait_ms: this.totalWaitMs(),
        ...this.milestoneFlags(),
        timeout_fired: this.timedOut,
      });
    }
    this.active = false;
    this.clearTimer();
  }

  /** The buyer took the slow-load escape hatch. */
  recordOpenedInNewTab() {
    trackEvent(FastCheckoutEvents.WIDGET_OPENED_IN_NEW_TAB, this.sharedProps());
  }

  /** Unmount — stop the timer without emitting anything. */
  dispose() {
    this.active = false;
    this.clearTimer();
  }

  private sharedProps() {
    return { ...this.getContext(), attempt: this.attempt, retry: this.retry };
  }

  /** Buyer-experienced wait since this attempt began (spans all retry cycles). */
  private totalWaitMs() {
    return Math.round(now() - this.attemptStartedAt);
  }

  private milestoneFlags() {
    return {
      iframe_load_fired: this.msToIframeLoad !== null,
      widget_ready_fired: this.msToWidgetReady !== null,
      height_seen: this.msToFirstHeight !== null,
    };
  }

  private clearTimer() {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
  }
}
